// components/EditarClienteForm.js — Editar cliente ÚNICO (formulario completo).
// UN solo formulario que edita TODOS los campos de un cliente Woo (nombre, celular,
// correo, cédula, cumpleaños, teléfonos y direcciones) y los guarda en UN solo PUT
// atómico a `/api/clientes/:id` con `{ ediciones:[...], confirmarDuplicado }`.
// Autocontenido (form + comparación pura + guardado + estilos propios). Reusa los
// utils de validación/presentación, no los reimplementa. NO conectado a ninguna
// página: el wiring lo hace el consumidor pasando `cliente`, `onGuardado`, `onCancel`.
import React, { useState } from 'react';
import { esCelularColombiano, esCorreoValido } from '../utils/celularColombiano';
import { esCorreoSintetico } from '../utils/correoCliente';
import CityAutocomplete, { ciudadDesdeSeleccion, ciudadDesdeTextoLibre } from './CityAutocomplete';
import { DEPARTAMENTOS, codigoDepartamento, esCodigoConocido, nombreDepartamento } from '../data/departamentosColombia';
import './EditarClienteForm.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ¿Cambió la dirección? Compara los cinco campos que el backend acepta.
function dirCambio(a, b) {
  return ['address_1', 'address_2', 'city', 'state', 'postcode']
    .some((k) => String(a?.[k] ?? '') !== String(b?.[k] ?? ''));
}

function normDir(d) {
  return {
    address_1: d?.address_1 ?? '',
    address_2: d?.address_2 ?? '',
    city: d?.city ?? '',
    state: d?.state ?? '',
    postcode: d?.postcode ?? '',
  };
}

// (a) FUNCIÓN PURA: compara el estado ORIGINAL con el form editado y devuelve el
// array `ediciones` (SOLO campos que cambiaron). Devuelve [] si nada cambió.
//   original = { first_name, last_name, email, cedula, birthday,
//                emailEsSintetico:boolean }
//   form     = { first_name, last_name, correo, cedula, cumpleanos,
//                telefonos:[{ original:<string|null>, valor:<string> }],
//                direcciones:[{ original:<obj|null>, valor:<obj> }] }
export function construirEdiciones(original, form) {
  const ediciones = [];

  // nombre
  const of = String(original.first_name ?? '');
  const ol = String(original.last_name ?? '');
  const nf = String(form.first_name ?? '');
  const nl = String(form.last_name ?? '');
  if (nf !== of || nl !== ol) {
    ediciones.push({ tipo: 'nombre', valorNuevo: { first_name: nf, last_name: nl } });
  }

  // correo — el input arranca vacío si el original es sintético; dejarlo vacío NO es
  // cambio; escribir uno real sí. Si el original es real, comparar contra él.
  const correoNuevo = String(form.correo ?? '').trim();
  const correoBase = original.emailEsSintetico ? '' : String(original.email ?? '').trim();
  if (correoNuevo !== correoBase) {
    ediciones.push({ tipo: 'correo', valorNuevo: correoNuevo });
  }

  // cédula
  const cedNueva = String(form.cedula ?? '').trim();
  if (cedNueva !== String(original.cedula ?? '').trim()) {
    ediciones.push({ tipo: 'cedula', valorNuevo: cedNueva });
  }

  // cumpleaños
  const cumpleNuevo = String(form.cumpleanos ?? '').trim();
  if (cumpleNuevo !== String(original.birthday ?? '').trim()) {
    ediciones.push({ tipo: 'cumpleanos', valorNuevo: cumpleNuevo });
  }

  // teléfonos
  (form.telefonos || []).forEach((t) => {
    const valor = String(t.valor ?? '').trim();
    if (t.original == null) {
      if (valor) ediciones.push({ tipo: 'telefono', modo: 'agregar', valorNuevo: valor });
    } else {
      const orig = String(t.original ?? '').trim();
      if (valor !== orig) {
        ediciones.push({ tipo: 'telefono', modo: 'editar', valorViejo: t.original, valorNuevo: valor });
      }
    }
  });

  // direcciones
  (form.direcciones || []).forEach((d) => {
    const valor = normDir(d.valor);
    if (d.original == null) {
      const tieneAlgo = Object.values(valor).some((v) => String(v).trim() !== '');
      if (tieneAlgo) ediciones.push({ tipo: 'direccion', modo: 'agregar', valorNuevo: valor });
    } else if (dirCambio(d.original, valor)) {
      ediciones.push({ tipo: 'direccion', modo: 'editar', valorViejo: d.original, valorNuevo: valor });
    }
  });

  return ediciones;
}

// (b) Hace el PUT atómico. Devuelve (sin lanzar):
//   { ok, cliente } | { dup:[coincidencias] } | { correoDup:mensaje } | { error:mensaje }
export async function guardarClienteBatch(clienteId, ediciones, confirmarDuplicado = false) {
  try {
    const res = await fetch(`${API_URL}/api/clientes/${clienteId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ediciones, confirmarDuplicado }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 409 && data.code === 'CELULAR_DUPLICADO') return { dup: data.coincidencias || [] };
    if (res.status === 409 && data.code === 'CORREO_DUPLICADO') return { correoDup: data.error || 'Ese correo ya está registrado en otro cliente.' };
    if (!res.ok) return { error: data.error || 'No se pudo guardar' };
    return { ok: true, cliente: data.cliente };
  } catch (e) {
    return { error: e.message };
  }
}

// Estado inicial del form a partir del `cliente` de entrada (forma mapPosCustomer).
function formDesdeCliente(cliente) {
  const emailEsSintetico = esCorreoSintetico(cliente.email);
  const telsBase = (cliente.phones && cliente.phones.length ? cliente.phones : (cliente.phone ? [cliente.phone] : []));
  const dirsBase = (cliente.addresses && cliente.addresses.length ? cliente.addresses : []);
  return {
    first_name: cliente.first_name || '',
    last_name: cliente.last_name || '',
    // arranca vacío si el original es sintético/vacío
    correo: emailEsSintetico ? '' : (cliente.email || ''),
    cedula: cliente.cedula || '',
    cumpleanos: cliente.birthday || '',
    telefonos: telsBase.map((t) => ({ original: t, valor: t })),
    direcciones: dirsBase.map((d) => ({ original: normDir(d), valor: normDir(d) })),
  };
}

// (c) COMPONENTE por defecto.
export default function EditarClienteForm({ cliente, onGuardado, onCancel }) {
  const emailEsSintetico = esCorreoSintetico(cliente.email);
  const original = {
    first_name: cliente.first_name || '',
    last_name: cliente.last_name || '',
    email: cliente.email || '',
    cedula: cliente.cedula || '',
    birthday: cliente.birthday || '',
    emailEsSintetico,
  };

  const [form, setForm] = useState(() => formDesdeCliente(cliente));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dup, setDup] = useState(null);
  const [correoDup, setCorreoDup] = useState(null);
  const [exito, setExito] = useState(false);

  const limpiar = () => { setError(null); setDup(null); setCorreoDup(null); setExito(false); };
  const setF = (patch) => { setForm((s) => ({ ...s, ...patch })); limpiar(); };

  const setTel = (i, valor) => setF({ telefonos: form.telefonos.map((t, j) => (j === i ? { ...t, valor } : t)) });
  const addTel = () => setF({ telefonos: [...form.telefonos, { original: null, valor: '' }] });

  const setDir = (i, campo, valor) => setDirCampos(i, { [campo]: valor });
  // Actualiza VARIOS campos de una dirección de una (elegir ciudad setea ciudad Y departamento en
  // UNA sola actualización — dos setF seguidos leerían el `form` viejo y el 2.º pisaría al 1.º).
  const setDirCampos = (i, patch) => setF({
    direcciones: form.direcciones.map((d, j) => (j === i ? { ...d, valor: { ...d.valor, ...patch } } : d)),
  });
  const addDir = () => setF({ direcciones: [...form.direcciones, { original: null, valor: normDir({}) }] });

  const guardar = async (confirmar = false) => {
    // Validación cliente-side ANTES de enviar.
    for (const t of form.telefonos) {
      const v = String(t.valor ?? '').trim();
      if (v && !esCelularColombiano(v)) {
        setError('El celular debe tener 10 dígitos y empezar por 3.');
        return;
      }
    }
    if (!esCorreoValido(form.correo)) {
      setError('El correo no tiene un formato válido.');
      return;
    }

    const ediciones = construirEdiciones(original, form);
    if (!ediciones.length) {
      setError('No hay cambios para guardar.');
      return;
    }

    setSaving(true);
    setError(null); setDup(null); setCorreoDup(null);
    const r = await guardarClienteBatch(cliente.id, ediciones, confirmar);
    setSaving(false);
    if (r.dup) { setDup(r.dup); return; }
    if (r.correoDup) { setCorreoDup(r.correoDup); return; }   // bloqueo duro
    if (r.error) { setError(r.error); return; }
    setExito(true);
    onGuardado(r.cliente);
  };

  const correoPlaceholder = (emailEsSintetico || !cliente.email) ? 'Sin correo' : 'Correo';

  return (
    <div className="ecf-form">
      <div className="ecf-title">Editar cliente</div>

      <div className="ecf-row">
        <label className="ecf-label">Nombre</label>
        <div className="ecf-two">
          <input className="ecf-input" placeholder="Nombre" value={form.first_name} onChange={(e) => setF({ first_name: e.target.value })} />
          <input className="ecf-input" placeholder="Apellido" value={form.last_name} onChange={(e) => setF({ last_name: e.target.value })} />
        </div>
      </div>

      <div className="ecf-row">
        <label className="ecf-label">Celular principal</label>
        <input
          className="ecf-input"
          placeholder="Celular (10 dígitos)"
          inputMode="tel"
          value={form.telefonos[0] ? form.telefonos[0].valor : ''}
          onChange={(e) => (form.telefonos.length ? setTel(0, e.target.value) : setF({ telefonos: [{ original: null, valor: e.target.value }] }))}
        />
      </div>

      <div className="ecf-row">
        <label className="ecf-label">Correo</label>
        <input className="ecf-input" placeholder={correoPlaceholder} inputMode="email" value={form.correo} onChange={(e) => setF({ correo: e.target.value })} />
      </div>

      <div className="ecf-row">
        <label className="ecf-label">Cédula</label>
        <input className="ecf-input" placeholder="Cédula" inputMode="numeric" value={form.cedula} onChange={(e) => setF({ cedula: e.target.value })} />
      </div>

      <div className="ecf-row">
        <label className="ecf-label">Cumpleaños</label>
        <input className="ecf-input" type="date" value={form.cumpleanos} onChange={(e) => setF({ cumpleanos: e.target.value })} />
      </div>

      <div className="ecf-row">
        <label className="ecf-label">Teléfonos adicionales</label>
        {form.telefonos.map((t, i) => (
          i === 0 ? null : (
            <input
              key={i}
              className="ecf-input"
              placeholder={t.original == null ? 'Nuevo teléfono' : 'Teléfono'}
              inputMode="tel"
              value={t.valor}
              onChange={(e) => setTel(i, e.target.value)}
            />
          )
        ))}
        <button type="button" className="ecf-add" onClick={addTel}>Agregar teléfono</button>
      </div>

      <div className="ecf-row">
        <label className="ecf-label">Direcciones</label>
        {form.direcciones.map((d, i) => (
          <div key={i} className="ecf-dir">
            <div className="ecf-dir-head">{i === 0 ? 'Principal' : (d.original == null ? 'Nueva dirección' : `Dirección ${i + 1}`)}</div>
            <label className="ecf-sublabel">Dirección</label>
            <input className="ecf-input" placeholder="Calle, carrera, número…" value={d.valor.address_1} onChange={(e) => setDir(i, 'address_1', e.target.value)} />
            <label className="ecf-sublabel">Complemento (apto, piso…)</label>
            <input className="ecf-input" placeholder="Apto, piso, torre…" value={d.valor.address_2} onChange={(e) => setDir(i, 'address_2', e.target.value)} />
            <label className="ecf-sublabel">Ciudad</label>
            <CityAutocomplete
              value={d.valor.city}
              placeholder="Ciudad"
              onPick={(m) => { const sel = ciudadDesdeSeleccion(m); setDirCampos(i, { city: sel.city, state: codigoDepartamento(sel.state) }); }}
              onFreeText={(t) => setDir(i, 'city', ciudadDesdeTextoLibre(t).city)}
            />
            <label className="ecf-sublabel">Departamento</label>
            <select className="ecf-input" value={d.valor.state} onChange={(e) => setDir(i, 'state', e.target.value)}>
              <option value="">— Departamento —</option>
              {/* Un valor que NO es un código ISO (CO-XXX) se conserva TAL CUAL como opción propia
                  (no se migra si no se toca): si es un corto legacy reconocido («ANT») se MUESTRA su
                  nombre; si es desconocido, el crudo + «(actual)». Elegir una opción estándar lo
                  cambia a CO-XXX. */}
              {d.valor.state && !esCodigoConocido(d.valor.state) && (
                <option value={d.valor.state}>{nombreDepartamento(d.valor.state) || `${d.valor.state} (actual)`}</option>
              )}
              {DEPARTAMENTOS.map((dep) => <option key={dep.codigo} value={dep.codigo}>{dep.nombre}</option>)}
            </select>
            <label className="ecf-sublabel">Código postal (opcional)</label>
            <input className="ecf-input" placeholder="Código postal (opcional)" value={d.valor.postcode} onChange={(e) => setDir(i, 'postcode', e.target.value)} />
          </div>
        ))}
        <button type="button" className="ecf-add" onClick={addDir}>Agregar dirección</button>
      </div>

      {error && <div className="ecf-warn">{error}</div>}
      {exito && <div className="ecf-ok">Cliente actualizado.</div>}
      {dup && (
        <div className="ecf-dup">
          <span>Ese celular ya está registrado en otro cliente{dup.length ? `: ${dup.map((c) => c.nombre || 'sin nombre').join(', ')}` : ''}.</span>
          <button type="button" className="ecf-dup-btn" onClick={() => guardar(true)}>Guardar de todas formas</button>
        </div>
      )}
      {correoDup && <div className="ecf-warn">{correoDup}</div>}

      <div className="ecf-actions">
        <button type="button" className="ecf-cancel" onClick={onCancel} disabled={saving}>Cancelar</button>
        <button type="button" className="ecf-save" onClick={() => guardar(false)} disabled={saving}>{saving ? 'Guardando…' : 'Guardar cambios'}</button>
      </div>
    </div>
  );
}
